import React, { useEffect, useState } from 'react';
import { ClockIcon, CalendarIcon, AlertCircleIcon } from 'lucide-react';
interface ViewingCountdownProps {
  date: string;
  time: string;
  status: 'scheduled' | 'rescheduled' | 'completed' | 'cancelled';
}
export function ViewingCountdown({
  date,
  time,
  status
}: ViewingCountdownProps) {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    isPast: boolean;
  }>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    isPast: false
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      const viewingDateTime = new Date(`${date}T${time}`);
      const now = new Date();
      const difference = viewingDateTime.getTime() - now.getTime();
      if (difference <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
          isPast: true
        };
      }
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor(difference % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
      const minutes = Math.floor(difference % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(difference % (1000 * 60) / 1000);
      return {
        days,
        hours,
        minutes,
        seconds,
        isPast: false
      };
    };
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [date, time]);
  // Don't show countdown for completed or cancelled viewings
  if (status === 'completed' || status === 'cancelled') {
    return null;
  }
  // Show "Necesită Feedback" if time has passed but not completed
  if (timeLeft.isPast) {
    return <div className="inline-flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-lg shadow-lg animate-pulse" data-id="element-5611">
        <AlertCircleIcon className="w-4 h-4" data-id="element-5612" />
        <span className="text-sm font-bold" data-id="element-5613">Necesită Feedback</span>
      </div>;
  }
  // Show countdown
  const getCountdownColor = () => {
    if (timeLeft.days === 0 && timeLeft.hours < 2) {
      return 'from-red-500 to-rose-600'; // Less than 2 hours - urgent
    } else if (timeLeft.days === 0 && timeLeft.hours < 24) {
      return 'from-orange-500 to-amber-600'; // Less than 24 hours - soon
    } else {
      return 'from-blue-500 to-indigo-600'; // Normal
    }
  };
  return <div className={`inline-flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r ${getCountdownColor()} text-white rounded-lg shadow-md`} data-id="element-5614">
      <ClockIcon className="w-4 h-4" data-id="element-5615" />
      <div className="flex items-center space-x-1 text-sm font-bold font-mono" data-id="element-5616">
        {timeLeft.days > 0 && <>
            <span data-id="element-5617">{timeLeft.days}z</span>
            <span className="opacity-50" data-id="element-5618">:</span>
          </>}
        <span data-id="element-5619">{String(timeLeft.hours).padStart(2, '0')}</span>
        <span className="opacity-50" data-id="element-5620">:</span>
        <span data-id="element-5621">{String(timeLeft.minutes).padStart(2, '0')}</span>
        <span className="opacity-50" data-id="element-5622">:</span>
        <span data-id="element-5623">{String(timeLeft.seconds).padStart(2, '0')}</span>
      </div>
    </div>;
}